// ═══════════════════════════════════════════════════════════════════════════
// grainRules.js — the grain guarantee/prevent-plant math GrainPosition needs,
// duplicated from src/App.jsx rather than hand-derived.
//
// WHY A DUPLICATE AND NOT AN IMPORT: App.jsx is a monolith that exports nothing
// (see AGENTS.md §1) — it default-exports the App component and every helper
// below lives as a bare top-level declaration in that same 85k-line file, mixed
// with JSX and browser-only code (useState, sbFetch, window/document). There is
// no clean import path into it from a new module, and AGENTS.md §7 / the Stage-1
// task both say to PREFER NEW FILES over touching App.jsx while other lanes may
// be working the same file concurrently.
//
// The right long-term fix mirrors copRules.js: move this cluster OUT of App.jsx
// into its own module and have App.jsx import it back, with a golden-file guard
// proving the move changed nothing (exactly what tools/check-cop-rules.js does
// for the COP extraction). That is a deliberate, reviewed App.jsx edit and is
// NOT done here — see ROADMAP.md §6 Phase II follow-up.
//
// Until then, this file is a byte-sourced duplicate: every function below was
// copied verbatim (not retyped, not paraphrased) from the cited App.jsx line
// range as of commit c2c3f66 (2026-08-17). tools/check-grain-rules.js extracts
// the SAME functions from the live App.jsx by source (the check-jd-guards.js
// technique) and asserts this module produces IDENTICAL output over real and
// synthetic cases. If the two ever drift, that checker fails loud — never a
// silent divergence between "what the farmer card shows" and "what the
// position engine shows".
//
// PURE ON PURPOSE — no React, no fetch, no Supabase, no module state.
// ═══════════════════════════════════════════════════════════════════════════

// ── src/App.jsx lines 1915-1929 — 2026 Prevent Plant Coverage Factors ───────
// EARP final rule (Nov 28 2025) removed PP buyup entirely for 2026+. One factor per crop.
export const PP_FACTORS = {
  "CORN": 0.55, "SOYBEANS": 0.60, "WHEAT": 0.60, "WINTER WHEAT": 0.60,
  "BARLEY": 0.60, "OATS": 0.60, "RYE": 0.60, "TRITICALE": 0.60,
  "GRAIN SORGHUM": 0.60, "DRY BEANS": 0.50, "PROCESSING BEANS": 0.40,
  "POTATOES": 0.45, "ONIONS": 0.35, "SILAGE SORGHUM": 0.50,
};
export function getPPFactor(crop) {
  if (!crop) return 0.55;
  const up = crop.toUpperCase();
  // Silage corn (GSS type) uses corn factor
  if (up === "SILAGE" || up === "CORN SILAGE") return PP_FACTORS["CORN"] || 0.55;
  return PP_FACTORS[up] || 0.55;
}

// ── src/App.jsx lines 1940-1949 ──────────────────────────────────────────────
// The RMA liability stored on a crop_coverage row is BLENDED — planted acres at
// the full revenue guarantee + prevent-plant acres at the reduced PP factor. The
// revenue guarantee a grower cares about is on PLANTED acres only (PP has its own
// guarantee, shown on the Prevent Plant tab). Given the blended liability, the
// total acres, and the prevent-plant acres (authoritative from the AIP M13 feed —
// crop_coverage.pp_acres), split out the planted portion while preserving RMA's
// acre-weighting:  liability = fullGuarPerAc × (planted + pp × ppFactor)
//   → plantedRev = liability × planted / (planted + pp × ppFactor)
// Returns identity (no change) when there's no prevent-plant on the cell.
export function plantedRevenueGuarantee(liability, acres, ppAcres, crop) {
  const A = Number(acres) || 0;
  const pp = Math.max(0, Number(ppAcres) || 0);
  if (pp <= 0) return { plantedAcres: acres == null ? null : A, plantedRev: liability == null ? null : Number(liability) };
  const planted = Math.max(0, A - pp);
  if (liability == null) return { plantedAcres: planted, plantedRev: null };
  const factor = getPPFactor(crop);
  const denom = planted + pp * factor;
  return { plantedAcres: planted, plantedRev: denom > 0 ? Number(liability) * planted / denom : 0 };
}

// ── src/App.jsx lines 2024-2025 ──────────────────────────────────────────────
export function plantedUnits(units) { return (units || []).filter(u => !u.is_prevent_plant); }
export function ppUnits(units)      { return (units || []).filter(u =>  u.is_prevent_plant); }

// ── src/App.jsx lines 2037-2058 ──────────────────────────────────────────────
// The exact planted liability, summed from the units RMA actually insured.
// plantedRevenueGuarantee() MODELS the split at the PP factor, which assumes ONE
// uniform guarantee/acre across the whole cell — true only when every unit shares
// an APH. Refuses (returns null, caller falls back to the model) unless the units
// reconcile to the stored acres AND every planted unit carries acres + liability.
export function plantedFromUnits(row, units) {
  const us = units || [];
  const pps = ppUnits(us);
  if (!pps.length) return null;              // no PP → the summary row is already planted-only
  const totAc = us.reduce((s, u) => s + (Number(u.acres) || 0), 0);
  const stored = Number(row.acres);
  if (!(totAc > 0) || !(stored > 0)) return null;
  if (Math.abs(totAc - stored) > Math.max(0.5, stored * 0.005)) return null;   // units don't account for the cell
  const planted = plantedUnits(us);
  if (!planted.length) return null;
  if (planted.some(u => u.acres == null || u.liability == null)) return null;
  const acres     = planted.reduce((s, u) => s + Number(u.acres), 0);
  const liability = planted.reduce((s, u) => s + Number(u.liability), 0);
  if (!(acres > 0)) return null;
  return {
    ppAcres: +(totAc - acres).toFixed(2),
    acres: +acres.toFixed(2),
    liability: +liability.toFixed(2),
    gpa: liability / acres,
    exact: true,
  };
}

// ── src/App.jsx lines 2004-2016 ──────────────────────────────────────────────
// The planted-acre view of a coverage row: acres, liability and guarantee/acre
// with prevent plant removed. RMA's own planted number wins whenever the
// per-unit rows can carry it; APH × price × coverage is only a fallback for
// rows with no liability keyed yet.
export function plantedView(row, ppAcres, estGuarPerAcre, units) {
  const exact = plantedFromUnits(row, units);
  if (exact) return exact;
  const pp = Math.max(0, Number(ppAcres) || 0);
  const split = plantedRevenueGuarantee(
    row.liability != null ? Number(row.liability) : null,
    row.acres != null ? Number(row.acres) : null, pp, row.crop);
  const acres = row.acres != null ? (pp > 0 ? split.plantedAcres : Number(row.acres)) : null;
  const liability = row.liability != null ? (pp > 0 ? split.plantedRev : Number(row.liability)) : null;
  const gpa = (liability != null && acres) ? liability / acres : (estGuarPerAcre ?? null);
  return { ppAcres: pp, acres, liability, gpa, exact: false };
}

// ── src/App.jsx — THE canonical bushel (production) guarantee ───────────────
// Both functions below are EXTRACTED FROM App.jsx BY SOURCE, not retyped. They
// had drifted in two directions at once and the drift was invisible:
//
//  1. `priceElectionPct` — RMA writes liability at acres x APH x coverage x
//     price x PRICE ELECTION PERCENT, so dividing by the price alone assumes a
//     100% election and returns a guarantee short by (1 - pct) on every row that
//     elected less. App.jsx gained the argument in dc9f39e; this copy kept the
//     five-argument version, so the same farm had two insured-bushel ceilings
//     depending on which grain surface you opened. Measured on the live CY2026
//     book, three grain farms read ~45% short — Bainbridge corn 117,810 against
//     214,199 bu. This is a forward-sell CEILING; a wrong one is permission to
//     sell bushels that are not covered.
//  2. `statedBu` / statedGuaranteeBu() — RMA STATES the per-unit guarantee at
//     P11 #120, and a stated number outranks anything derived. Added upstream
//     later; this copy never got it either.
//
// WHY BOTH SURVIVED A DEDICATED DRIFT CHECKER: tools/check-grain-rules.js called
// BOTH copies with FIVE arguments, and the canonical defaults a missing election
// to 100% — so the two agreed, on the wrong number. It then CRASHED outright once
// statedGuaranteeBu appeared, because that name was never added to its extraction
// list, and a crash exits non-zero without ever comparing anything. The checker
// now pins the arity of both copies and asserts an ABSOLUTE CAT value, which is
// the only thing that survives both copies regressing together.
export function statedGuaranteeBu(units) {
  if (!Array.isArray(units) || !units.length) return null;
  const planted = units.filter(u => !u.is_prevent_plant);
  if (!planted.length) return null;
  let total = 0;
  for (const u of planted) {
    const ac = Number(u.acres) || 0;
    if (!(ac > 0)) return null;
    const q = Number(u.acre_guarantee_qty) || 0;
    if (q > 0) { total += q * ac; continue; }   // RMA stated it outright (NAU)
    // No #120. Only a TIMELY unit is derivable — an adjusted one (L/M/P/E) carries
    // a factor that is NOT in the file, and returning a full guarantee on those
    // acres would overstate exactly where the grower is already short.
    if (u.acreage_status) return null;
    const aph = Number(u.aph_yield) || 0, cov = Number(u.coverage_level) || 0;
    if (!(aph > 0 && cov > 0)) return null;
    total += aph * cov * ac;
  }
  return total > 0 ? total : null;
}

export function guaranteeBushels(liability, price, aph, cov, acres, priceElectionPct, statedBu) {
  // RMA's own stated guarantee outranks anything derived — see statedGuaranteeBu().
  if (statedBu != null && Number(statedBu) > 0) return Number(statedBu);
  const pct = (priceElectionPct == null || !(Number(priceElectionPct) > 0))
    ? 1 : Number(priceElectionPct);
  const effectivePrice = price > 0 ? price * pct : 0;
  if (liability != null && effectivePrice > 0) return Number(liability) / effectivePrice;
  return (aph > 0 && cov > 0 && acres > 0) ? aph * cov * acres : null;
}

// ── src/App.jsx lines 3401-3407 ──────────────────────────────────────────────
export function normalizeCounty(county) {
  if (!county) return county;
  return county.replace(/\s+[NPDB]$/i, "").trim().toUpperCase();
}

// ── src/App.jsx lines 2084-2089 ──────────────────────────────────────────────
// Key normalisation matters: crop_coverage stores type_code NULL for the "997"
// (unknown) sentinel and NULL for practice "003" (non-irrigated), while the unit
// rows keep the raw codes. Both sides collapse to "" here so a card row finds
// its own units instead of silently finding none.
export const cuKey = (county, crop, plan, typeCode, practiceCode) => {
  const tc = (typeCode && typeCode !== "997") ? String(typeCode) : "";
  const pc = (practiceCode && practiceCode !== "003") ? String(practiceCode) : "";
  return `${normalizeCounty(county || "").toUpperCase().trim()}|${(crop || "").toUpperCase()}|` +
         `${(plan || "").toUpperCase()}|${tc}|${pc}`;
};

// ── src/App.jsx lines 2179-2185 — THE planted split ──────────────────────────
// Pass unitsByKey and it uses RMA's own per-unit sum; omit it and it falls back
// to the PP-factor model.
export function plantedSplit(row, ppAcres, unitsByKey) {
  const units = unitsByKey
    ? unitsByKey[cuKey(row.county, row.crop, row.plan,
                       row.typeCode ?? row.type_code, row.practiceCode ?? row.practice_code)]
    : null;
  return plantedView(row, ppAcres, null, units);
}

// ── src/App.jsx lines 2081-2089-ish (weightedAph) ────────────────────────────
// Acre-weighted APH across a coverage's units — what RMA actually prices off.
// Returns null when acres are missing, so callers fall back to the stored value.
export function weightedAph(units) {
  if (!units || units.length < 2) return null;
  let ac = 0, sum = 0;
  for (const u of units) {
    const a = Number(u.acres) || 0, y = Number(u.aph_yield);
    if (!(a > 0) || !Number.isFinite(y) || y <= 0) continue;
    ac += a; sum += y * a;
  }
  return ac > 0 ? +(sum / ac).toFixed(1) : null;
}

// ── src/App.jsx lines 2353-2392 — RMA_SPRING_PRICES ──────────────────────────
// The rung-2 fallback price table (verified constants) grainBushelsFromCoverage
// falls back to when a coverage row's own price_election is unset (which CLAUDE.md
// documents is essentially every row book-wide — the AIP file never carries the
// dollar price). This is NOT the full resolveRmaPrice() ladder (rung 1 reads the
// live rma_prices table, rung 3 is a futures estimate) — grainBushelsFromCoverage
// itself only ever uses this constant as fallbackPx, so duplicating the ladder's
// other rungs here would not change this function's behavior. A caller that wants
// the live-feed price should resolve it and pass it through price_election on the
// rows it hands in.
export const RMA_SPRING_PRICES = {
  2026: {
    CORN: 4.62, SOYBEANS: 11.09, WHEAT: 5.76, BARLEY: 5.45,
    "GRAIN SORGHUM": 4.61, OATS: 3.30, TRITICALE: 3.51,
    POTATOES: 17.85,
    CABBAGE: 24.55,
    "SWEET CORN": 130.00,
    "PROCESSING BEANS": 365.00,
  },
  2025: { CORN: 4.70, SOYBEANS: 10.54, WHEAT: 6.06, POTATOES: 10.00, CABBAGE: 24.55 },
  2024: { CORN: 4.66, SOYBEANS: 11.55, WHEAT: 6.72 },
  2023: { CORN: 5.91, SOYBEANS: 13.76, WHEAT: 8.45 },
};

// ── src/App.jsx lines 71079-71142 — grainBushelsFromCoverage ────────────────
// Liability-first, never-blend-coverage-lines grain exposure resolver. See
// CLAUDE.md "NEVER BLEND A CROP'S COVERAGE LINES TO GET A GUARANTEE" for the
// history of what this replaced (Math.max coverage level, NULL-acres rows
// silently zero-weighting the APH average, tonnage folded into a bushel figure).
// ── ORGANIC IS ITS OWN MARKETING CROP ───────────────────────────────────────
//
// A farmer asked for this and the book backs him up: organic and conventional
// are not one position. RMA rates and PRICES them separately — the CY2026 wheat
// price election is $5.76 conventional against $11.16 organic — so blending the
// two produces a guarantee that is neither, and a forward-sell ceiling built on
// it is wrong in a direction nobody can see.
//
// MEASURED on the live book (CY2026 crop_coverage, practice 713/714):
//
//     CORN      27 rows · 10 farms · 1,338.1 ac
//     SOYBEANS  12 rows ·  8 farms ·   441.4 ac
//     WHEAT      5 rows ·  3 farms ·   162.7 ac
//
// and TEN farm/crop positions carry BOTH practices at once. Palladino and
// Carley’s corn is 383.5 organic against 228.3 conventional — 63% organic,
// shown as one blended row. W Cleveland’s corn is 53.5% organic.
//
// ── THE PRACTICE TRAVELS INSIDE THE CROP NAME ──────────────────────────────
// "ORGANIC CORN" is the marketing identity; CORN is still the insurance crop
// underneath it, and nothing here rewrites crop, type, practice or crop year on
// a coverage row. Carrying it in the name is what lets every existing caller of
// grainBushelsFromCoverage inherit the split without a new argument — the same
// eight call sites, one decoder.
//
// ⚠ TRANSITIONAL (714) COUNTS AS ORGANIC HERE AND THAT IS A CHOICE. RMA rates
// it on the organic tables and prices it as organic, which is what decides a
// guarantee, so for a MARKETING position it belongs with organic. It is not the
// same thing agronomically and a grower may not be able to sell it as certified;
// isCertifiedOrganicPractice is kept separate so a future surface that needs
// that distinction has it without re-deriving one.
const ORGANIC_PRACTICE_CODES = new Set(["713", "714", "702", "712"]);
export function isOrganicPractice(code) {
  return ORGANIC_PRACTICE_CODES.has(String(code || "").trim());
}
export function isCertifiedOrganicPractice(code) {
  const c = String(code || "").trim();
  return c === "713" || c === "702";
}

// Is THIS coverage row organic? The code first, because it is RMA’s own
// statement; the label only where no code was captured (the hand-loaded books
// carry a practice name and no code).
export function isOrganicRow(r) {
  const code = (r && (r.practice_code ?? r.practiceCode));
  if (code != null && String(code).trim() !== "") return isOrganicPractice(code);
  return /ORGANIC|TRANSITIONAL/i.test(String((r && r.practice) || ""));
}

// "ORGANIC CORN" -> { base: "CORN", organic: true }.  "Corn" -> { base: "CORN",
// organic: false }. One decoder, so a caller cannot invent a second spelling.
export function grainCropParts(marketingCrop) {
  const t = String(marketingCrop || "").trim().toUpperCase().replace(/\s+/g, " ");
  if (t.startsWith("ORGANIC ")) return { base: t.slice(8).trim(), organic: true };
  return { base: t, organic: false };
}
export function organicCropName(baseCrop) {
  return "ORGANIC " + String(baseCrop || "").trim().toUpperCase();
}

// WHICH ORGANIC ROWS THIS FARM ACTUALLY HAS INSURED — Kevin’s condition:
// "This should only appear if they have insured organic acres." A farm with no
// organic coverage sees exactly the three crops it sees today, so the feature is
// invisible to the 270 farms it does not apply to.
//
// ACRES ARE THE TEST, not the mere existence of a row: a CY2027 organic shell
// with null acreage is a coverage that has not been reported yet, and putting an
// empty ORGANIC CORN row on the page reads as a position the grower does not
// have. A row with acres > 0 is a farm that grows it.
// ── WHICH PRACTICE A CONTRACT WILL BE FILLED FROM ─────────────────────────
//
// Kevin, 2026-09-05: "someones organic corn contracts show against their
// organic acres, and their conventional corn contracts show against their
// conventional acres/insurance guarantees."
//
// THREE ANSWERS, AND THE THIRD IS THE ONE THAT MATTERS:
//
//   "organic" / "conventional"  — the contract SAYS so (grain_contracts.practice)
//   the only practice this farm insures for that commodity — DERIVED, not guessed
//   null                        — UNASSIGNED, and shown as such
//
// ⚠ THE DERIVED CASE IS WHAT KEEPS 270 FARMS WORKING. Every one of the 646
// contracts in the book carries no practice, because nothing in an elevator
// file states one. If an unmarked contract were simply UNASSIGNED, every farm
// in the book would read 0% committed overnight — a far worse number than the
// blend this change exists to remove. A farm that insures corn under ONE
// practice has only one practice its corn contracts can be filled from; that
// is a reading of the coverage, not an assumption about the contract.
//
// ⚠ AN UNMARKED CONTRACT IS CONVENTIONAL. Kevin, 2026-09-05, on seeing the
// first version: "we took a step back by losing all the existing corn and
// wheat contracts, can you recover those and put those under conventional corn
// and wheat?"
//
// The earlier rule refused to attribute an unmarked contract on a farm
// insuring both practices, on the reasoning that guessing moves a forward-sell
// ceiling. That reasoning is sound and the CONSEQUENCE was worse than the
// risk: R L JEFFRES' 17 corn contracts (310,000 bu) and 11 wheat contracts
// (167,037 bu) left every card reading 0 contracts and 0% committed, with
// 477,037 bu sitting in a callout instead of against the crop they were sold
// against. A ceiling that says "you have sold nothing" is not more cautious
// than one that says "you have sold this much conventional" — it is wrong in
// the direction that invites overselling.
//
// The default is defensible on the data as well as the instruction: not one of
// the 646 contracts in the book carries a practice, because the column did not
// exist when they were written, while organic is a minority of acres on every
// farm that has any. Conventional is the answer that is right far more often
// than it is wrong, and the Practice control on the contract is how a grower
// corrects it — one click, on the row, per contract.
//
// ⚠ IT IS STILL A DEFAULT AND THE PAGE SAYS SO. On a farm insuring both, the
// conventional card names how many of its contracts are there by default
// rather than by statement. Silent is what would make this a guess.
// The day grain_contracts.practice began to exist (sql/062). Before it, a
// contract COULD NOT record a practice, so its silence says nothing about the
// crop; after it, silence is somebody not filling the field in.
//
// Measured 2026-09-06 against the live book: all 646 contracts were created
// between 2026-05-14 and 2026-09-04, and ZERO after the column existed. So the
// grandfathering below moves every contract in the book today and no future
// one, which is exactly the split it is meant to draw.
export const GRAIN_PRACTICE_COLUMN_ADDED = "2026-09-05";

function contractPredatesPractice(contract) {
  const t = contract && (contract.created_at || contract.createdAt);
  if (!t) return false;   // undateable is NOT grandfathered - see the note below
  const d = String(t).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) && d < GRAIN_PRACTICE_COLUMN_ADDED;
}

// ⚠ LEGACY AND FUTURE ARE DIFFERENT QUESTIONS, AND ONE RULE ANSWERED BOTH.
//
// Kevin, 2026-09-05, on 28 contracts and 477,037 bu vanishing off R L JEFFRES'
// cards: "recover those and put those under conventional corn and wheat." That
// governs the contracts already in the book, and the reasoning is sound for
// them: they were written when the column did not exist, so their silence is
// OUR schema’s silence, not the grower’s.
//
// ⚠⚠ IT DOES NOT GOVERN A CONTRACT WRITTEN TOMORROW. Once the field is
// there to fill in, an empty one means somebody skipped it — and defaulting
// that to conventional would quietly classify a future ORGANIC contract as
// conventional, moving a forward-sell ceiling on evidence that does not exist.
// A legacy default is grandfathering; the same default on new data is a guess.
//
// So: stated wins; one insured practice is read from the coverage; a contract
// predating the column is CONVENTIONAL by explicit grandfathering; anything
// later with nothing stated is UNASSIGNED and named on screen.
//
// ⚠ AN UNDATEABLE CONTRACT IS NOT GRANDFATHERED. `created_at` defaults to
// now() and every one of the 646 rows carries it, so this is unreachable today
// — but a row with no date cannot be PROVEN to predate the column, and
// grandfathering on an assumption is the thing this split exists to stop.
export function grainContractPractice(contract, practicesOnFarm) {
  const stated = String((contract && contract.practice) || "").trim().toLowerCase();
  if (stated === "organic" || stated === "conventional") return stated;
  const set = [...new Set(practicesOnFarm || [])];
  if (set.length === 1) return set[0];
  return contractPredatesPractice(contract) ? "conventional" : null;
}

// On the conventional card only because it predates the practice column.
// Reported so a grandfathered default never passes as a statement.
export function grainContractPracticeIsDefaulted(contract, practicesOnFarm) {
  const stated = String((contract && contract.practice) || "").trim().toLowerCase();
  if (stated === "organic" || stated === "conventional") return false;
  if ([...new Set(practicesOnFarm || [])].length <= 1) return false;
  return contractPredatesPractice(contract);
}

// Written since the column existed, on a farm insuring both, and still says
// nothing. Not counted on either card — somebody has to choose.
export function grainContractPracticeIsUnassigned(contract, practicesOnFarm) {
  const stated = String((contract && contract.practice) || "").trim().toLowerCase();
  if (stated === "organic" || stated === "conventional") return false;
  if ([...new Set(practicesOnFarm || [])].length <= 1) return false;
  return !contractPredatesPractice(contract);
}

// Is this contract on the conventional card only because nobody said otherwise?
// Used to report the default rather than let it pass as a statement.

// Which practices this farm insures for one BASE commodity, in the crop year on
// screen. Derived from coverage with ACRES — a shell row with no acreage is a
// coverage not yet reported, and letting it create a second practice would
// push every contract on the farm into UNASSIGNED for a position the grower
// does not yet have.
export function grainPracticesForCrop(coverageRows, baseCrop, cropYear = null) {
  const t = String(baseCrop || "").toUpperCase();
  const out = new Set();
  for (const r of (coverageRows || [])) {
    if (!r) continue;
    if (cropYear != null && r.crop_year != null && Number(r.crop_year) !== Number(cropYear)) continue;
    const u = String(r.crop || "").toUpperCase();
    const same = t === "WHEAT" ? (u === "WHEAT" || u === "WINTER WHEAT") : u === t;
    if (!same || !(Number(r.acres) > 0)) continue;
    out.add(isOrganicRow(r) ? "organic" : "conventional");
  }
  return [...out];
}

// Does this contract belong on this MARKETING crop's card?
// "ORGANIC CORN" takes organic corn contracts; "Corn" takes conventional ones
// AND the ones that state no practice — those are still SOLD, and are reported
// separately as needing a practice rather than dropped from the total.
export function contractMatchesMarketingCrop(contract, marketingCrop, coverageRows, cropYear = null) {
  const parts = grainCropParts(marketingCrop);
  const u = String((contract && contract.crop) || "").toUpperCase();
  const same = parts.base === "WHEAT" ? (u === "WHEAT" || u === "WINTER WHEAT") : u === parts.base;
  if (!same) return false;
  const prac = grainContractPractice(contract, grainPracticesForCrop(coverageRows, parts.base, cropYear));
  // ⚠⚠ AN UNASSIGNED CONTRACT IS STILL SOLD. Returning false here put it on
  // NEITHER card, and the bushels vanished out of the crop's headline.
  //
  // Kevin, 2026-09-10: "W Cleveland Farms has 20,000 bushels of corn sold but
  // the dashboard shows 15,000." Measured: four CY2026 corn contracts, 5,000 bu
  // each. Three were created in August and are grandfathered to conventional;
  // the fourth was hand-entered 2026-09-09, after the practice column existed,
  // so it was UNASSIGNED and counted nowhere. The dashboard's own average gave
  // it away — (5.00+5.01+4.96)/3 = $4.99 against four contracts.
  //
  // ⚠ AND THE ARITHMETIC WAS ALREADY INCONSISTENT, which is the part that makes
  // this a defect rather than a preference. The DENOMINATOR is
  // commodityTotalBu — the commodity's whole production, BOTH practices, and
  // its own comment names this farm: "Measuring % committed against the
  // conventional half alone would report W Cleveland as roughly twice as sold
  // as he is." A numerator that drops a practice-less contract against a
  // both-practices denominator can only understate.
  //
  // ⚠ UNDERSTATING SOLD BUSHELS IS THE DANGEROUS DIRECTION. A farm that reads
  // less sold than it is invites the next sale. This file already carries the
  // mirror-image rule for the guarantee — a dropped coverage line makes the
  // ceiling read short and % sold read high, "so it can never be silent".
  //
  // What the 2026-09-05 split was actually protecting is intact: nothing here
  // claims the contract is conventional. grainContractPractice still returns
  // null, grainContractPracticeIsUnassigned still reports it, and the card
  // still names the bushels as needing a practice. This decides only WHICH CARD
  // COUNTS THE BUSHELS, and on a commodity-wide denominator that is the base
  // card — never the organic one, which would be the guess.
  if (prac == null) return !parts.organic;
  return (prac === "organic") === parts.organic;
}

// The contracts sitting on the conventional card BY DEFAULT — the farm insures
// both practices for this commodity and the contract does not say which it
// will fill. They are counted and marketed as conventional (see the rule
// above); this is what lets the page say so instead of implying somebody
// chose. Empty on every farm that insures one practice, which is most of them.
function sameBaseCrop(contract, baseCropUpper) {
  const u = String((contract && contract.crop) || "").toUpperCase();
  return baseCropUpper === "WHEAT" ? (u === "WHEAT" || u === "WINTER WHEAT") : u === baseCropUpper;
}

export function defaultedPracticeContracts(contracts, baseCrop, coverageRows, cropYear = null) {
  const t = String(baseCrop || "").toUpperCase();
  const practices = grainPracticesForCrop(coverageRows, t, cropYear);
  return (contracts || []).filter((c) => sameBaseCrop(c, t) && grainContractPracticeIsDefaulted(c, practices));
}

// ⚠ A DIFFERENT LIST, BECAUSE IT IS A DIFFERENT FACT. A grandfathered
// contract is COUNTED as conventional and merely reported; an unassigned one is
// counted on NEITHER card and is somebody’s to place. Folding them together
// would either hide the second or re-open the first.
export function unassignedPracticeContracts(contracts, baseCrop, coverageRows, cropYear = null) {
  const t = String(baseCrop || "").toUpperCase();
  const practices = grainPracticesForCrop(coverageRows, t, cropYear);
  return (contracts || []).filter((c) => sameBaseCrop(c, t) && grainContractPracticeIsUnassigned(c, practices));
}

export function grainOrganicCropsPresent(coverageRows, baseCrops, cropYear = null) {
  const out = [];
  for (const base of (baseCrops || [])) {
    const t = String(base).toUpperCase();
    const hit = (coverageRows || []).some((r) => {
      if (!r || !isOrganicRow(r)) return false;
      if (cropYear != null && r.crop_year != null && Number(r.crop_year) !== Number(cropYear)) return false;
      const u = String(r.crop || "").toUpperCase();
      const same = t === "WHEAT" ? (u === "WHEAT" || u === "WINTER WHEAT") : u === t;
      return same && Number(r.acres) > 0;
    });
    if (hit) out.push(organicCropName(base));
  }
  return out;
}
// REPORTED PRODUCTION, SPLIT THE SAME WAY THE POSITION IS.
//
// Harvested bushels arrive as production_history rows carrying a practice, and
// if they are not split the organic bushels land on the conventional row while
// the organic row reads "never reported" — the blend this whole change exists
// to remove, one surface further along.
//
// ⚠ THE RESOLVER IS INJECTED RATHER THAN IMPORTED. src/reportedProduction.js is
// CommonJS and this file is ESM; importing across that line would either force a
// module-system change on a file ten checkers read BY SOURCE, or (the worse
// option) leave a second copy of the newest-policy-wins rule here to drift.
// Passing it in keeps exactly one definition of each rule.
//
// ⚠ AND THIS IS DELIBERATELY NOT DONE INSIDE reportedProductionForCrop. The
// farmer card asks a different question with the same words: there, CORN means
// the insurance crop and SHOULD include every practice written under it. Only a
// marketing position splits, so only the marketing caller asks for a split.
//
// A row whose practice was never captured counts as conventional — the same
// reading isOrganicRow gives everywhere — but it is COUNTED and returned as
// practiceUnknown, because "not organic" and "we were not told" are different
// facts and a silent default is how organic acres would go missing. Measured
// 2026-09-05 across all 10 farms in the book carrying organic grain coverage:
// ZERO of their 997 production_history rows have a null practice, so this counts
// nothing today and exists for the row somebody writes tomorrow.
export function grainReportedByMarketingCrop(rows, marketingCrops, resolveForCrop) {
  const out = {};
  for (const mc of (marketingCrops || [])) {
    const parts = grainCropParts(mc);
    const scoped = (rows || []).filter((r) => isOrganicRow(r) === parts.organic);
    const got = resolveForCrop(scoped, parts.base);
    if (!got) continue;
    const unknown = parts.organic ? 0 : scoped.filter((r) => {
      const code = (r && (r.practice_code ?? r.practiceCode));
      const hasCode = code != null && String(code).trim() !== "";
      return !hasCode && !String((r && r.practice) || "").trim();
    }).length;
    out[mc] = unknown ? { ...got, practiceUnknown: unknown } : got;
  }
  return out;
}

export function grainBushelsFromCoverage(rows, grainCrop, year = null, unitsByKey = null) {
  // The marketing crop may carry the practice ('ORGANIC CORN'); the insurance
  // crop underneath is still CORN. Decoding here means all eight call sites get
  // the split without a new argument, and a farm with no organic coverage is
  // unaffected because nothing ever asks for an organic crop it does not have.
  const parts = grainCropParts(grainCrop);
  const target = parts.base;
  const isMatch = c => { const u = String(c || '').toUpperCase(); return target === 'WHEAT' ? (u === 'WHEAT' || u === 'WINTER WHEAT') : u === target; };
  // A CONVENTIONAL position must EXCLUDE organic rows, not merely fail to ask
  // for them — otherwise splitting the book leaves the organic acres counted
  // twice, once on their own row and once inside conventional.
  const practiceOk = r => isOrganicRow(r) === parts.organic;
  const isTopUp = p => /ECO|SCO|ARPI/i.test(String(p || ''));
  // Corn insures as GRAIN (GSG, bu/ac) or SILAGE (GSS/026, ton/ac). Grain marketing
  // is bushels — never fold a tonnage line into a bushel guarantee (units invariant).
  const isSilage = r => { const t = String(r.type || '').toUpperCase(); return t === 'GSS' || t === '026'; };
  const fallbackPx = year != null ? (RMA_SPRING_PRICES[year]?.[target] || 0) : 0;
  let guar = 0, exp = 0, acres = 0, covNum = 0, covDen = 0, aphNum = 0, aphDen = 0, liability = 0, any = false;
  let ppAcresTot = 0, ppSeen = false, ppExact = false;
  const incomplete = [];
  (rows || []).filter(r => isMatch(r.crop) && practiceOk(r) && !isTopUp(r.plan) && !isSilage(r)).forEach(r => {
    const aph = Number(r.aph_yield) || 0, cov = Number(r.coverage_level) || 0;
    const pe = Number(r.price_election) || 0;
    const px = pe > 0 ? pe : fallbackPx;
    // PREVENT PLANT OFF THE TOP, through the shared rule. `pp_acres` NULL means NOT
    // DETERMINED, never zero — but a row that never selected the column is also
    // null, and those are not the same thing. Flagged below rather than guessed at.
    const ppAc = r.pp_acres != null ? Number(r.pp_acres) || 0 : 0;
    const split = plantedSplit(r, ppAc, unitsByKey);
    if (ppAc > 0 || (split.ppAcres || 0) > 0) { ppSeen = true; ppAcresTot += split.ppAcres || ppAc; if (split.exact) ppExact = true; }
    const ac  = split.acres != null ? Number(split.acres) : 0;
    const lib = split.liability != null ? Number(split.liability) : 0;
    // RMA STATES this per unit (#120 x acres); prefer it over anything derived.
    // Same lookup plantedSplit() just used, so the two cannot disagree about
    // which units belong to this row.
    const uRows  = unitsByKey
      ? unitsByKey[cuKey(r.county, r.crop, r.plan, r.typeCode ?? r.type_code, r.practiceCode ?? r.practice_code)]
      : null;
    const stated = statedGuaranteeBu(uRows);
    const uGuar = guaranteeBushels(lib > 0 ? lib : null, px, aph, cov, ac, (r.priceElectionPct ?? r.price_election_pct), stated);
    if (uGuar == null) { incomplete.push({ county: r.county || '—', why: 'no liability and no APH/acres' }); return; }
    any = true;
    guar += uGuar;
    if (lib > 0) liability += lib;
    exp += cov > 0 ? uGuar / cov : uGuar;
    if (ac > 0) { acres += ac; if (cov > 0) { covNum += cov * ac; covDen += ac; } if (aph > 0) { aphNum += aph * ac; aphDen += ac; } }
    else incomplete.push({ county: r.county || '—', why: 'liability keyed but acres not reported' });
  });
  if (!any) return null;
  const ppUnknown = (rows || []).some(r => isMatch(r.crop) && !isTopUp(r.plan) && !isSilage(r)
                                           && !('pp_acres' in r));
  if (ppUnknown) incomplete.push({ county: '—', why: 'prevent-plant acres not loaded — any PP is still counted as planted' });
  return {
    guaranteeBu: guar || null,
    expectedBu: exp || null,
    acres: acres || null,
    liability: liability || null,
    coverage: covDen > 0 ? covNum / covDen : null,
    aph: aphDen > 0 ? aphNum / aphDen : null,
    ppAcres: ppSeen ? +ppAcresTot.toFixed(2) : 0,
    ppExact,
    incomplete,
  };
}

// ── ACRES INSURED AS GRAIN THAT GET CHOPPED ─────────────────────────────────
// `grainBushelsFromCoverage` above already drops lines insured as SILAGE (GSS /
// 026) — a tonnage line must never enter a bushel guarantee. This is the other
// half, and the file cannot see it: a GSG line the grower CHOPS is insured as
// grain, has liability keyed as grain, and sits inside the grain acres. R L
// Jeffres is the case — 6,540 GSG acres, a large share of them chopped.
//
// THE POLICY DOES NOT CHANGE. RMA still insures those acres and still pays on
// them — it appraises the silage and converts to bushels — so the farmer card,
// the PDF and every insurance surface keep the full guarantee. The grain number
// is a different question: a pre-harvest SELL CEILING, where a chopped acre
// delivers no bushels into a contract. Selling against it is precisely the
// exposure the ceiling exists to prevent.
//
// IT LIVES HERE, NOT IN App.jsx, BECAUSE IT HAS TWO CALLERS AND MUST NOT GROW A
// SECOND COPY. The per-farm Marketing Tool applies it in `cropInsurance`; the
// book-wide Grain Book View and the crop-year continuity strip apply it through
// `buildGrainPosition`. It shipped in App.jsx alone first, and the two screens
// disagreed about the same farm within the hour — Kevin, 2026-08-20: "Book view
// should definitely update based on crop plan." Same "one model, two callers"
// rule as grainTargets.js / miYieldModel.js / copRules.js.
//
// PRO-RATA ON ACRES, never on a modelled yield. The guarantee is already
// liability-first and acre-weighted per coverage line, so scaling by the share
// of acres removed keeps the exact per-acre guarantee RMA stated. Running the
// chopped acres through an APH instead would re-derive a number the file already
// gives — the blend-the-coverage-lines mistake in a new costume.
//
// Returns the SAME shape it was given plus `chop`, so a caller that ignores the
// extra field is unchanged, and a caller that reads it can say what was removed.
// `chopped: 0` (the default) returns the figures untouched.
export function grainChopAdjust(b, choppedAcres) {
  const base = b || null;
  const asked = Number(choppedAcres) || 0;
  const insured = Number(base?.acres) || 0;
  if (!base || asked <= 0) {
    return base && { ...base, chop: { chopped: 0, grainAcres: insured || null, share: 1, capped: false, asked: 0 } };
  }
  // A grower can plan to chop more acres than the grain lines hold — usually
  // because some of it is already insured as GSS and counted nowhere here. Cap
  // it and SAY SO rather than returning a negative guarantee.
  const capped = asked > insured;
  const chopped = capped ? insured : asked;
  const grainAcres = Math.max(0, insured - chopped);
  const share = insured > 0 ? grainAcres / insured : 0;
  const scale = (v) => (v == null ? v : v * share);
  return {
    ...base,
    guaranteeBu: scale(base.guaranteeBu),
    expectedBu: scale(base.expectedBu),
    // Liability is what RMA PAYS on and does not move when a grower chops — it is
    // left alone deliberately, so the insurance dollar figure beside the bushel
    // figure keeps agreeing with the policy.
    chop: { chopped, grainAcres, share, capped, asked },
  };
}

// The grower's chopped-acre estimate for one (crop) out of already-fetched
// grain_crop_plan rows. One lookup rule, so a caller cannot quietly match on a
// different crop-name case than the resolver does.
// WHICH CROPS CAN BE CHOPPED — grain corn, and nothing else.
//
// The chopped-acre haircut exists for ONE agronomic fact: acres RMA insured as
// GRAIN corn (GSG) that go through a chopper instead of a combine. They grow a
// crop and deliver no bushels into a contract, so the forward-sell ceiling has
// to come down while the policy does not move.
//
// Soybeans and wheat are not chopped for silage, and the control was offered on
// them anyway — every crop in GRAIN_CROPS rendered the same box. Measured on the
// live book 2026-09-04: 9 of 9 soybean and wheat plan rows carry no chopped
// figure, so nobody had used it yet. LATENT IS NOT FIXED — the box invited a
// grower to shorten a wheat sell ceiling for a reason that does not exist, and
// nothing on screen would have said so afterwards.
//
// TYPE-AWARE, NOT MERELY CROP-AWARE. Corn insured as SILAGE (GSS / 026) is
// ALREADY silage production: grainBushelsFromCoverage drops those lines before a
// bushel figure exists, so asking how much of it will be chopped would haircut a
// number that was never grain. Asserted rather than assumed.
//
// The CROP WORD, never a substring. POPCORN and SWEET CORN are different products
// that do not sit in this grain position, and `ORGANIC CORN` — the marketing
// identity a separated organic position would carry — is grain corn and does.
export function cropChopsForSilage(crop) {
  // Normalised to WORDS: anything that is not a letter becomes a space, runs of
  // spaces collapse, and the result is padded so a word test needs no \b — an
  // escape that has to survive a shell is an escape that silently becomes a
  // backspace character, and a regex full of those matches nothing while looking
  // exactly right in the source.
  const t = String(crop || '').toUpperCase().replace(/[^A-Z]+/g, ' ').replace(/ +/g, ' ').trim();
  if (!t) return false;
  const w = ' ' + t + ' ';
  // Already silage — it was never in a grain position to be taken out of.
  if (w.indexOf(' SILAGE ') >= 0 || w.indexOf(' GSS ') >= 0) return false;
  // Different products that do not sit in this grain marketing position.
  if (w.indexOf(' SWEET ') >= 0 || w.indexOf(' POP ') >= 0 || w.indexOf(' SEED ') >= 0) return false;
  // The crop WORD, never a substring: POPCORN must not match on CORN.
  return t === 'CORN' || t.endsWith(' CORN');
}

export function choppedAcresFor(planRows, crop) {
  // REFUSED AT THE RULE, not only hidden in the UI. A stored value on a crop
  // that cannot be chopped — typed before this rule existed, or written by any
  // other path — must not haircut a sell ceiling just because the box is gone.
  if (!cropChopsForSilage(crop)) return 0;
  const t = String(crop || '').toUpperCase();
  const row = (planRows || []).find(r => String(r.crop || '').toUpperCase() === t);
  return Number(row?.chopped_acres) || 0;
}
